import { useEffect, useState, useRef } from 'react';
import { Globe, Building2, Trophy, Award, Users } from 'lucide-react';

const stats = [
  { icon: Globe, value: 6, suffix: '+', label: 'Regions', description: 'Global Presence' },
  { icon: Building2, value: 15, suffix: '+', label: 'Industries', description: 'Sectors Covered' },
  { icon: Trophy, value: 100, suffix: '+', label: 'Countries', description: 'Worldwide Reach' },
  { icon: Award, value: 20000, suffix: '+', label: 'Global Brands', description: 'Trusted By' },
  { icon: Users, value: 1000, suffix: '+', label: 'Winners', description: 'Excellence Honored' },
];

const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration, start]);

  return { count, ref };
};

const StatCard = ({ icon: Icon, value, suffix, label, description }) => {
  const { count, ref } = useCountUp(value, 2500);

  return (
    <div
      ref={ref}
      className="group text-center p-8"
    >
      {/* Icon */}
      <div className="mb-6 flex justify-center">
        <div className="w-20 h-20 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors duration-300">
          <Icon className="w-10 h-10 text-[#D4AF37]" strokeWidth={1.5} />
        </div>
      </div>

      {/* Number */}
      <div className="mb-3">
        <span className="font-montserrat text-5xl sm:text-6xl text-[#D4AF37] counter-number font-bold">
          {count.toLocaleString()}
        </span>
        <span className="font-montserrat text-4xl sm:text-5xl text-[#D4AF37] font-bold">{suffix}</span>
      </div>

      {/* Label */}
      <h3 className="font-montserrat text-white text-xl font-semibold mb-2">{label}</h3>
      <p className="font-google-sans text-[#A1A1AA] text-base">{description}</p>
    </div>
  );
};

export const CounterSection = () => {
  return (
    <section data-testid="counter-section" className="py-20 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full px-4 sm:px-8 lg:px-16 relative z-10">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 border border-white/10 divide-x divide-y divide-white/10 lg:divide-y-0">
          {stats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </div>
      </div>
    </section>
  );
};
